import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { matchPath, useLocation } from 'react-router-dom';

import { BottomNav } from './BottomNav';
import { getEstablishmentByHost, listEstablishments } from '@/services/client';
import { useCart } from '@/store/cart.store';
import { applyTheme } from '@/utils/theme';

// Фолбэк на первое заведение: включаем только для локальных хостов,
// чтобы в проде по незарегистрированному домену не "прилипало" случайное.
function isLocalHost(h: string): boolean {
  // Только "настоящая" локалка. Кастомные *.local / *.test домены
  // через /etc/hosts считаются полноценными — они должны резолвиться по /by-host.
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '0.0.0.0' ||
    h === '::1' ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(h)
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const estId = useCart((s) => s.establishmentId);
  const setEst = useCart((s) => s.setEstablishment);

  // В dev можно форсить host через ?host=... или localStorage('resstro_host_override').
  // Удобно тестить multi-domain меню без правки /etc/hosts.
  // ВАЖНО: override применяется ТОЛЬКО когда реальный хост — localhost/127.0.0.1/IP.
  // На реальных доменах (прод и /etc/hosts) старый override из localStorage может «залипнуть»
  // и сломать резолв по host — поэтому игнорируем его и чистим запись.
  const realHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const realIsLocal = isLocalHost(realHost);
  const override = (() => {
    if (typeof window === 'undefined') return '';
    if (!realIsLocal) {
      localStorage.removeItem('resstro_host_override');
      return '';
    }
    const q = new URLSearchParams(window.location.search).get('host');
    if (q) {
      localStorage.setItem('resstro_host_override', q);
      return q;
    }
    return localStorage.getItem('resstro_host_override') ?? '';
  })();
  const host = override || realHost;
  const isLocal = !override && realIsLocal;

  if (import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[layout] host resolve', { realHost, override, host, isLocal });
  }

  // 1) резолв по домену
  const { data: byHost, isFetched: hostFetched } = useQuery({
    queryKey: ['client/ests/by-host', host],
    queryFn: () => getEstablishmentByHost(host),
    enabled: !!host && !isLocal,
    staleTime: 30 * 1000, // Небольшой кэш — тема быстро подтягивается после правок в админке.
    refetchOnWindowFocus: true,
    retry: false,
  });

  // 2) Список заведений — нужен всегда в dev (для фолбэка по estId и применения темы),
  //    и в проде только если host-резолв не дал результата.
  //    Без этого при уже сохранённом estId applyTheme не получал data и цвет не обновлялся.
  const { data: all } = useQuery({
    queryKey: ['client/ests'],
    queryFn: listEstablishments,
    enabled: isLocal || (hostFetched && !byHost),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    // Приоритет у резолва по домену: если хост даёт заведение, всегда синхронизируем
    // estId с ним, даже если в localStorage было что-то другое. Без этого клиент,
    // который раньше выбрал другое заведение, не переключался при заходе на «свой» домен.
    if (byHost?.id) {
      if (byHost.id !== estId) setEst(byHost.id);
      return;
    }
    if (estId) return;
    if (isLocal && all && all.length > 0) {
      setEst(all[0].id);
    }
  }, [estId, byHost, all, isLocal, setEst]);

  // Применяем брендинг-тему, как только узнали заведение.
  // Для dev-фолбэка берём first из all, чтобы цвета не слетали.
  useEffect(() => {
    const current =
      byHost ??
      all?.find((e) => e.id === estId) ??
      (isLocal ? all?.[0] : null) ??
      null;
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.debug('[layout] theme source', {
        fromHost: !!byHost,
        fromAllById: !!all?.find((e) => e.id === estId),
        estId,
        current,
      });
    }
    applyTheme(current?.theme ?? null, {
      establishmentName: current?.name,
      host: host || null,
    });
  }, [byHost, all, estId, isLocal, host]);

  // На детальной странице блюда таббар прячем (как в Wolt/Я.Еда):
  // там фиксированная кнопка «+ В корзину», таббар мешает и перекрывает её.
  const { pathname } = useLocation();
  const hideBottomNav = !!matchPath('/menu/:id', pathname);

  return (
    <div className={`min-h-full max-w-lg mx-auto ${hideBottomNav ? '' : 'pb-20'}`}>
      <main className={hideBottomNav ? '' : 'pb-4'}>{children}</main>
      {!hideBottomNav && <BottomNav />}
    </div>
  );
}
